<!--
  This modal works through Vuex, it enables easier debugging
  using Vue Devtools. It's attached to mrVueApp and can be called
  from anywhere within the app. Just pass in a path to a component you
  wish to display in the modal.

  Ensure that component gets registered somewhere, usually in the component it will be used from.

      import AddressEditModal from './AddressEditModal';

      export default {
        name: "AddressBook",
        components: {
          AddressEditModal,
          AddressDeleteModal
        },

  To open it dispatch showModal to the state passing:
      methods: {
        editAddressModal() {
          this.$store.dispatch('modal/showModal', { component: './AddressBook/AddressEditModal' });
        }
      }

  In template:
      button(@click="editAddressModal()") Open Your modal

-->
<template lang="pug">
    .vue-app-modal.xs-f-small.max-at-tweak(:class="classes")
      transition(name="modal-background-fade")
        .modal-backdrop.bg-black-a40(v-if="visible" @click.self="backgroundClick")
      transition(name="modal-body-fade")
        focus-lock.app-modal(v-if="visible" :disabled="!visible")
          .modal-body(role="dialog" tabindex="0")
            component(:is="component" v-bind="modalProps" @exit-modal="exitModal" @hide-modal="hideModal")

            button.btn-close.transparent-btn.xs-f-large.max-at-tweak(type="button" @click="exitModal" aria-label="Close modal" data-mr-ass="close-modal")
              i.icon-x.clickable

          button.btn-back.transparent-btn.xs-f-large.max-at-tweak(v-if="stepBackActions.length" type="button" @click="stepBack" aria-label="Go back")
            i.icon-arrow-back.clickable

</template>

<script>
  const bodyScrollLock = require('body-scroll-lock');
  const disableBodyScroll = bodyScrollLock.disableBodyScroll;
  const enableBodyScroll = bodyScrollLock.enableBodyScroll;
  const clearAllBodyScrollLocks = bodyScrollLock.clearAllBodyScrollLocks;

  import Vue from 'vue';
  import { mapState, mapActions } from 'vuex';
  export default {
    name: "AppModal",

    data() {
      return {
        component: null
      };
    },

    mounted() {
      document.body.addEventListener('keydown', e => {
        if (e.keyCode === 27) {
          this.exitModal();
        }
      })
    },

    computed: {
      ...mapState('modal', [
        'visible',
        'theme',
        'persistent',
        'modalComponent',
        'modalProps',
        'stepBackActions'
      ]),

      classes() {
        if (this.visible) {
          disableBodyScroll(this.$el, {
            allowTouchMove: el => {
              while (el && el !== document.body) {
                if (el.getAttribute('body-scroll-lock-ignore') !== null) {
                  return true
                }
          
                el = el.parentNode
              }
            }
          });
        } else {
          enableBodyScroll(this.$el);
        }
        return [this.theme, this.visible ? 'visible' : ''];
      }
    },

    methods: {
      ...mapActions('modal', [
        'hideModal',
        'executeStepBackAction'
      ]),

      exitModal() {
        this.$emit('exitModal');
        this.hideModal();
        this.executeStepBackAction();
      },

      backgroundClick() {
        if (this.persistent) {
          return;
        }

        this.exitModal();
      },

      stepBack() {
        this.hideModal();
        this.executeStepBackAction();
      }
    },

    watch: {
      modalComponent(componentPath) {
        if (!componentPath) {
          return;
        }

        var componentName = componentPath.split('/').pop();
        Vue.component(componentName, () => import(`${componentPath}`));

        this.component = componentName;
      },
      stepBackActions(actions) {
        if(!actions.length) {
          clearAllBodyScrollLocks();
        }
      }
    }
  }
</script>

<style lang="stylus">
  .vue-app-modal
    -webkit-overflow-scrolling touch
    
    &.visible
      position fixed
      top 0
      right 0
      bottom 0
      left 0
      overflow auto
      z-index 9999999
      
    .modal-backdrop
      position fixed
      top 0
      bottom 0
      left 0
      right 0
      z-index 200

    [data-lock]
      height 100%
      width 100%
      overflow auto
      
    .app-modal
      position absolute
      top 3em
      left 50%
      transform(translateX(-50%))
      max-width 100%
      z-index 201
      background-color color-white
      border 1px solid color-fog-light
      border-radius 8px
      overflow hidden
      margin-bottom 5em

    .modal-body
      padding 2em 10% 2em
      overflow-x auto
      overflow-y auto
      margin auto
      position relative

    .btn-close
      z-index 10
      top 0
      right 0
      position absolute
      padding 0.5em
      color color-gray-triple-nines

    .btn-back
      top 0
      left 0
      position absolute
      padding 0.5em
      color color-gray-triple-nines

    .modal-background-fade-enter-active
    .modal-background-fade-leave-active
      transition opacity .2s

    .modal-background-fade-enter
    .modal-background-fade-leave-to
      opacity 0

    .modal-body-fade-enter-active
    .modal-body-fade-leave-active
      transition all .3s

    .modal-body-fade-enter
    .modal-body-fade-leave-to
      top 0em
      opacity 0

    @media mq-mobile-tweak-lower
      .app-modal
        top 0
        left 0
        right 0
        border none
        border-radius 0
        transform none
        margin-bottom 0

        .modal-body
          padding 1em 3% 2em
    
    @media mq-mobile-tweak-upper
      .app-modal
        width 20em
        max-width 90%
        
        .modal-body
          padding 1em 3% 2em

    @media mq-tablet
      .app-modal
        width 43em
        max-width 90%
        
        .modal-body
          padding 1em 8% 2em

    @media mq-desktop-md-plus
      .app-modal
        width 48em
        max-width 90%

      .narrow-modal-body
        width 33em
        margin 0 auto
  
    &.no-padding
      .modal-body
        padding 0
        
    &.try-it-on-modal
      .app-modal
        .modal-body
          padding 0
          
      @media mq-mobile
        .app-modal
          width 100%
          height 100%
          max-width 100%
          top 0
          margin 0
          border-radius 0
          
          .btn-close
            display none

      @media mq-tablet
        .app-modal
          width 25em
      
      @media mq-desktop-md-plus
        .app-modal
          background-color color-fog-light
          
    &.full-screen
      .app-modal
        width 100%
        height 100%
        max-width 100%
        top 0
        margin 0
        border-radius 0
        
        div[data-lock]
          width 100%
          height 100%
          
        .modal-body
          width 100%
          height 100%
          padding 0
          
        .btn-close
          display none

</style>
